import { useState, useEffect } from "react";
import { Navigate, Routes, Route, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Menu from "./components/Views/Menu";
import Dashboard from "./components/Views/Dashboard";
import GettingStarted from "./components/Views/GettingStarted";
import AccountView from "./components/Views/AccountView";
import FeedbackForm from "./components/Views/FeedbackForm";
import Careers from "./components/Lists/Careers";
import CareerEdit from "./components/Edits/CareerEdit";
import PersonSkills from "./components/Lists/PersonSkills";
import SkillCalendar from "./components/Views/SkillCalendar";
import SkillTestRunner from "./components/Views/SkillTestRunner";
import SkillForum from "./components/Views/SkillForum";
import EmploymentHistory from "./components/Lists/EmploymentHistory";
import Connections from "./components/Lists/Connections";
import ChatView from "./components/Views/ChatView";
import EmployerEditRoute from "./components/Views/EmployerEditRoute";
import PersonSkillEdit from "./components/Edits/PersonSkillEdit";
import "./style/scss/App.scss";
import Recruit from "./components/Views/Recruit";
import RecruiterDashboard from "./components/Views/RecruiterDashboard";
import EmployerDashboard from "./components/Views/EmployerDashboard";
import JobWorkspace from "./components/Views/JobWorkspace";
import PersonEdit from "./components/Edits/PersonEdit";
import Register from "./components/Views/RegisterForm";
import ResumeView from "./components/Views/ResumeView";
import AgreementSignature from "./components/Edits/AgreementSignature";
import EmployerAdminEdit from "./components/Edits/EmployerAdminEdit";
import Invite from "./components/Edits/InviteEdit";
import EmployerRoleEdit from "./components/Edits/EmployerRoleEdit";
import JobMatchReview from "./components/Views/JobMatchReview";
import ScreeningInviteLanding from "./components/Views/ScreeningInviteLanding";
import CoworkerVerify from "./components/Views/CoworkerVerify";
import JobInterestResponse from "./components/Views/JobInterestResponse";
import PackageItemEdit from "./components/Edits/PackageItemEdit";
import AddEmployerVIP from "./components/Edits/AddEmployerVIP";
import EmployerRecruitEdit from "./components/Edits/EmployerRecruitEdit";
import EmployerView from "./components/Views/EmployerView";

import AdminDashboardView from "./components/Admin/Admin";
import AdminAgreements from "./components/Admin/AdminAgreements";
import AdminSkillEdit from "./components/Admin/AdminSkillEdit";
import AdminEmployerEdit from "./components/Admin/AdminEmployerEdit";
import AdminPackageItemTypes from "./components/Admin/AdminPackageItemTypes";
import JobIntakeList from "./components/Views/JobIntakeList";
import JobIntakeDetail from "./components/Views/JobIntakeDetail";
import LogoutForm from "./components/Controls/LogoutForm";
import RegisterCode from "./static/RegisterCode";
import Blacklist from "./static/blacklist";
import { RequireHiring, RequireSysAdmin } from "./components/Controls/AccessGates";

import MarketingShell from "./marketing/MarketingShell";
import DecisionPage from "./marketing/pages/DecisionPage";
import ForSeekers from "./marketing/pages/ForSeekers";
import ForEmployers from "./marketing/pages/ForEmployers";
import HowItWorks from "./marketing/pages/HowItWorks";
import About from "./marketing/pages/About";
import Faq from "./marketing/pages/Faq";
import Contact from "./marketing/pages/Contact";
import Sms from "./marketing/pages/Sms";
import SmsOptIn from "./marketing/pages/SmsOptIn";
import Privacy from "./marketing/pages/Privacy";
import Terms from "./marketing/pages/Terms";
import SmsJoin from "./marketing/pages/SmsJoin";
import VerifyEmail from "./marketing/pages/VerifyEmail";
import ResetPassword from "./marketing/pages/ResetPassword";
import MustChangePasswordGate from "./components/Controls/MustChangePasswordGate";
import RequiredAgreementsGate from "./components/Controls/RequiredAgreementsGate";
import { getAppMode, RECRUITER } from "./mode/useAppMode";
import { usePersonSkillsQuery } from "./query/usePersonSkills";
import { useCareersQuery } from "./query/useCareers";

const PUBLIC_PATHS = [
  "/",
  "/login",
  "/logout",
  "/register",
  "/register-code",
  "/blacklist",
  "/about",
  "/faq",
  "/contact",
  "/for-seekers",
  "/for-employers",
  "/how-it-works",
  "/sms",
  "/sms-opt-in",
  "/privacy",
  "/terms",
  "/sms-join",
  "/verify-email",
  "/reset-password",
  "/screening",
  "/skillTest",
  "/coworker-verify",
];

function isPublicPath(pathname) {
  const path = (pathname || "/").toLowerCase();
  return PUBLIC_PATHS.some(
    (p) => path === p || path.startsWith(`${p}?`) || path.startsWith(`${p}/`)
  );
}

function clearAuthSession() {
  window.sessionStorage.removeItem("BearerToken");
  window.sessionStorage.removeItem("UserId");
  window.sessionStorage.removeItem("SessionId");
  window.sessionStorage.removeItem("MustChangePassword");
}

function RedirectSkillForum() {
  const [params] = useSearchParams();
  const skillId = Number(params.get("skillId")) || 0;
  return <Navigate to={skillId > 0 ? `/skillForum/${skillId}` : "/skills"} replace />;
}

export default function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(
    !!window.sessionStorage.getItem("BearerToken")
  );
  const [mustChangePassword, setMustChangePassword] = useState(
    () => window.sessionStorage.getItem("MustChangePassword") === "1"
  );
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoggedIn && !isPublicPath(location.pathname)) {
      clearAuthSession();
      navigate("/");
    }
  }, [isLoggedIn, location.pathname, navigate]);

  useEffect(() => {
    setMustChangePassword(window.sessionStorage.getItem("MustChangePassword") === "1");
  }, [isLoggedIn, location.pathname]);

  const { data: skillsData, isLoading: skillsLoading, isFetching: skillsFetching } =
    usePersonSkillsQuery({ enabled: !!isLoggedIn && !mustChangePassword });
  const { data: careersData, isLoading: careersLoading, isFetching: careersFetching } =
    useCareersQuery({ enabled: !!isLoggedIn && !mustChangePassword });

  useEffect(() => {
    if (!isLoggedIn || mustChangePassword) return;
    const path = (location.pathname || "/").toLowerCase();
    if (path.startsWith("/getting-started") || path.startsWith("/logout") || path.startsWith("/agreement")) {
      return;
    }
    if (window.sessionStorage.getItem("SkipGettingStarted") === "1") return;
    // Recruiters working in hiring mode are not setting up a seeker profile.
    if (getAppMode() === RECRUITER) return;
    if (skillsLoading || careersLoading || skillsFetching || careersFetching) return;
    if (!skillsData || !careersData) return;
    const skills = skillsData.personSkills || [];
    const careers = careersData.careers || [];
    if (skills.length < 3 || careers.length === 0) {
      navigate("/getting-started");
    }
  }, [
    isLoggedIn,
    mustChangePassword,
    location.pathname,
    navigate,
    skillsLoading,
    careersLoading,
    skillsFetching,
    careersFetching,
    skillsData,
    careersData,
  ]);

  return (
    <div className="App">
      {isLoggedIn ? (
        <>
          {mustChangePassword ? (
            <MustChangePasswordGate
              onComplete={() => setMustChangePassword(false)}
            />
          ) : null}
          <Menu updateLogin={setIsLoggedIn} />
          <RequiredAgreementsGate>
          <Routes>
            <Route exact path="/" element={<Dashboard />} />
            <Route path="/getting-started" element={<GettingStarted />} />
            <Route path="/about" element={<About />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/sms" element={<Sms />} />
            <Route path="/sms-opt-in" element={<SmsOptIn />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/sms-join" element={<SmsJoin />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/account" element={<AccountView />} />
            <Route path="/feedback" element={<FeedbackForm />} />
            <Route path="/skills" element={<PersonSkills />} />
            <Route path="/skillForum/:skillId" element={<SkillForum />} />
            <Route path="/skillTests" element={<RedirectSkillForum />} />
            <Route path="/skillCalendar" element={<SkillCalendar />} />
            <Route path="/skillTest/:id" element={<SkillTestRunner />} />
            <Route path="/screening/:token" element={<ScreeningInviteLanding />} />
            <Route path="/coworker-verify/:token" element={<CoworkerVerify />} />
            <Route path="/skillQuestions" element={<RedirectSkillForum />} />
            <Route path="/personSkill/:id?" element={<PersonSkillEdit />} />
            <Route path="/employments" element={<EmploymentHistory />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/connections" element={<Connections />} />
            <Route path="/chat/:id" element={<ChatView />} />
            <Route path="/employerEdit/:id?" element={<EmployerEditRoute />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/careers/:id" element={<CareerEdit />} />
            <Route path="/resume/:id" element={<ResumeView />} />
            <Route path="/recruit" element={<RequireHiring><Recruit /></RequireHiring>} />
            <Route path="/recruiter" element={<RequireHiring><RecruiterDashboard /></RequireHiring>} />
            <Route path="/company/:id" element={<RequireHiring><EmployerDashboard /></RequireHiring>} />
            <Route path="/jobWorkspace/:id" element={<RequireHiring><JobWorkspace /></RequireHiring>} />
            <Route path="/personEdit/:id?" element={<PersonEdit />} />
            <Route path="/register" element={<Register />} />
            <Route path="/register-code" element={<RegisterCode />} />
            <Route path="/blacklist" element={<Blacklist />} />
            <Route path="/agreementSignature/:id" element={<AgreementSignature />} />
            <Route path="/invite" element={<Invite />} />
            <Route path="/employerAdminEdit/:id" element={<RequireHiring><EmployerAdminEdit /></RequireHiring>} />
            <Route path="/employerRoleEdit/:id?" element={<RequireHiring><EmployerRoleEdit /></RequireHiring>} />
            <Route path="/jobMatches/:id" element={<RequireHiring><JobMatchReview /></RequireHiring>} />
            <Route path="/jobInterest/:id" element={<JobInterestResponse />} />
            <Route path="/packageItemEdit/:id?" element={<RequireHiring><PackageItemEdit /></RequireHiring>} />
            <Route path="/addEmployerVIP" element={<RequireHiring><AddEmployerVIP /></RequireHiring>} />
            <Route path="/employerRecruitEdit/:id?" element={<RequireHiring><EmployerRecruitEdit /></RequireHiring>} />
            <Route path="/admin" element={<RequireSysAdmin><AdminDashboardView /></RequireSysAdmin>} />
            <Route path="/adminAgreements" element={<RequireSysAdmin><AdminAgreements /></RequireSysAdmin>} />
            <Route path="/adminSkillEdit/:id" element={<RequireSysAdmin><AdminSkillEdit /></RequireSysAdmin>} />
            <Route path="/adminEmployerEdit/:id" element={<RequireSysAdmin><AdminEmployerEdit /></RequireSysAdmin>} />
            <Route path="/adminPackageItemTypes" element={<RequireSysAdmin><AdminPackageItemTypes /></RequireSysAdmin>} />
            <Route path="/job-intake" element={<RequireSysAdmin><JobIntakeList /></RequireSysAdmin>} />
            <Route path="/job-intake/:id" element={<RequireSysAdmin><JobIntakeDetail /></RequireSysAdmin>} />
            <Route path="/employerView/:id" element={<EmployerView />} />
            <Route path="/logout" element={<LogoutForm updateLogin={setIsLoggedIn} />} />
          </Routes>
          </RequiredAgreementsGate>
        </>
      ) : (
        <MarketingShell updateLogin={setIsLoggedIn}>
          <Routes>
            <Route path="/" element={<DecisionPage />} />
            <Route path="/login" element={<DecisionPage />} />
            <Route path="/logout" element={<DecisionPage />} />
            <Route path="/for-seekers" element={<ForSeekers />} />
            <Route path="/for-employers" element={<ForEmployers />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/register" element={<DecisionPage />} />
            <Route path="/register-code" element={<RegisterCode />} />
            <Route path="/blacklist" element={<Blacklist />} />
            <Route path="/about" element={<About />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/sms" element={<Sms />} />
            <Route path="/sms-opt-in" element={<SmsOptIn />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/sms-join" element={<SmsJoin />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/screening/:token" element={<ScreeningInviteLanding />} />
            <Route path="/coworker-verify/:token" element={<CoworkerVerify />} />
            <Route path="/skillTest/:id" element={<SkillTestRunner />} />
          </Routes>
        </MarketingShell>
      )}
    </div>
  );
}
